/** Mirrors backend AmazonCategorySalesRankEntry / Creators API browse node rank lines. */
export interface CategorySalesRankEntry {
  categoryDisplayName?: string | null
  salesRank?: number | null
}

export interface ProductData {
  productId?: string | null
  url?: string | null
  productName?: string | null
  imageUrl?: string | null
  description?: string | null
  retailPrice?: number | null
  salePrice?: number | null
  finalPrice?: number | null
  /** Overall site / global rank when returned by Amazon Creators API. */
  websiteSalesRank?: number | null
  websiteSalesRankLabel?: string | null
  categorySalesRanks?: CategorySalesRankEntry[] | null
  minOrderQuantity?: number | null
  maxOrderQuantity?: number | null
}

export interface DiscountInfo {
  type: string // Serialized from DiscountType enum (DollarAmount or Percent)
  value: number
  retailPrice?: number | null
  salePrice?: number | null
  finalSalePrice?: number | null
  requiresCouponOrPromoCode: boolean
}

export interface CouponInfo {
  hasCoupon: boolean
  couponCode?: string | null
  couponType?: string | null
  couponValue?: number | null
}

/** Observations keyed by source id (e.g. `postText.regexPrices`, `amazon.paApi`). */
export type ObservationMap = Record<string, Record<string, unknown>>

/** Mirrors backend `PostToDealEvidence` from single-call pipeline responses. */
export interface PostToDealEvidence {
  text?: ObservationMap
  prices?: ObservationMap
  discounts?: ObservationMap
  promoCodes?: ObservationMap
  urls?: ObservationMap
  productIds?: ObservationMap
  productSnapshots?: ObservationMap
  availability?: ObservationMap
  coupons?: ObservationMap
  captcha?: ObservationMap
  finalDeal?: FinalDealObservation | null
}

export interface FinalDealObservation {
  source?: string
  capturedAtUtc?: string
  link?: string | null
  productId?: string | null
  retailPrice?: number | null
  finalPrice?: number | null
  dealType?: number
  promoCode?: string | null
  couponInfo?: CouponInfo | null
  /** Backend FinalDealPricingBasis — how retail/final were chosen (see POST_TO_DEAL_LOGIC.md). */
  pricingBasis?: string | null
  note?: string | null
}

export default class ScrapedPostData {
  // Step 1: Product Data
  parsedUrl?: string | null
  finalizedUrl?: string | null
  productId?: string | null
  productData?: ProductData | null
  isOutOfStock?: boolean
  hasPromoCodeOrCoupon?: boolean

  // Step 2: Discount
  normalizedText?: string | null
  nerResult?: any | null // TextAnalysis_ExecuteResponse - not used by frontend
  nerLabels?: string[] | null
  nerEntitiesByLabel?: Record<string, string[]> | null
  discountInfo?: DiscountInfo | null

  // Step 3: Promo Code
  promoCode?: string | null

  // Step 4: Coupon Code
  couponInfo?: CouponInfo | null

  // Deal Type
  dealType?: number | null

  // Captcha Detection
  isCaptcha?: boolean

  /** Accumulated pipeline evidence + finalDeal summary — must persist across steps. */
  evidence?: PostToDealEvidence | null

  constructor(data?: Partial<ScrapedPostData>) {
    if (data) {
      this.parsedUrl = data.parsedUrl
      this.finalizedUrl = data.finalizedUrl
      this.productId = data.productId
      this.productData = data.productData
      this.isOutOfStock = data.isOutOfStock ?? false
      this.hasPromoCodeOrCoupon = data.hasPromoCodeOrCoupon ?? false
      this.normalizedText = data.normalizedText
      this.nerResult = data.nerResult
      this.nerLabels = data.nerLabels
      this.nerEntitiesByLabel = data.nerEntitiesByLabel
      this.discountInfo = data.discountInfo
      this.promoCode = data.promoCode
      this.couponInfo = data.couponInfo
      this.dealType = data.dealType ?? 0
      this.isCaptcha = data.isCaptcha ?? false
      this.evidence = data.evidence ?? null
    }
  }
}
